import { isEqual } from 'lodash';

import {
  WorkflowsCreatePayloadArtifactParallelConfig,
  WorkflowsUpdateArtifactConfigPayload,
  WorkflowsUpdateArtifactPayload,
  WorkflowsUpdatePayload,
} from '@api/types.gen';

import {
  StaggerConfig,
  StaggerOption,
} from '@/react/edge/edge-stacks/components/StaggerFieldset.types';

import { WorkflowArtifact } from '../types';
import { Artifact, ArtifactConfig, FormValues } from '../wizard/types';
import {
  toEnvVarsPayload,
  toFilesPayload,
  toParallelDeploymentConfig,
} from '../wizard/payload';

/**
 * Builds a partial update from what the user actually changed: unchanged fields
 * and entirely unchanged artifacts are omitted, and the endpoint leaves anything
 * omitted alone. Artifacts are matched to `workflow.artifacts` by position,
 * which is how `toEditFormValues` laid them out.
 */
export function buildUpdateWorkflowPayload(
  values: FormValues,
  initialValues: FormValues,
  artifacts: Array<WorkflowArtifact>
): WorkflowsUpdatePayload {
  if (values.engine !== initialValues.engine) {
    return toMigrationPayload(values, artifacts);
  }

  const changedArtifacts = artifacts
    .map((artifact, index) =>
      toArtifactUpdate(
        artifact,
        values.artifacts[index],
        initialValues.artifacts[index]
      )
    )
    .filter(isArtifactUpdate);

  return {
    name: changed(values.name, initialValues.name),
    artifacts: changedArtifacts.length > 0 ? changedArtifacts : undefined,
    // The interval belongs to the workflow, not to any one of its artifacts.
    reconcile:
      values.engine === 'reconcile' &&
      values.reconcile.interval !== initialValues.reconcile.interval
        ? { interval: values.reconcile.interval }
        : undefined,
  };
}

/**
 * Builds the request that moves a workflow onto the reconciliation engine.
 *
 * The endpoint converts every artifact itself, so the entries here only carry
 * what the new deployments need: the reconcile configuration and the targets.
 * They are keyed by the Edge stack ids the artifacts still have, which is what
 * the workflow looks like until the request lands.
 *
 * Everything is sent rather than only what changed. The deployments do not
 * exist yet, so there is no recorded choice for an omitted field to fall back
 * on.
 */
function toMigrationPayload(
  values: FormValues,
  artifacts: Array<WorkflowArtifact>
): WorkflowsUpdatePayload {
  return {
    name: values.name,
    engine: 'reconcile',
    reconcile: { interval: values.reconcile.interval },
    artifacts: artifacts.map((artifact, index) => ({
      id: artifact.id,
      type: artifact.type,
      targets: {
        edgeGroups: values.artifacts[index].targets,
        environmentGroups: values.artifacts[index].environmentGroups,
        environments: values.artifacts[index].environments,
      },
      config: toReconciledConfig(values.artifacts[index].config),
    })),
  };
}

/** Every reconcile setting, for a deployment that has none recorded yet. */
function toReconciledConfig(
  config: ArtifactConfig
): WorkflowsUpdateArtifactConfigPayload {
  return {
    driftMode: config.driftMode,
    useManifestNamespaces: config.useManifestNamespaces,
    kubernetesPrune: config.kubernetesPrune,
    kubernetesForceApply: config.kubernetesForceApply,
  };
}

function toArtifactUpdate(
  artifact: WorkflowArtifact,
  values: Artifact | undefined,
  initialValues: Artifact | undefined
): WorkflowsUpdateArtifactPayload | undefined {
  if (!values || !initialValues) {
    return undefined;
  }

  // The artifact name is deliberately absent: the endpoint cannot update it.
  const isReconciled = artifact.type === 'gitopsDeployment';

  const files = changed(
    toFilesPayload(values.files),
    toFilesPayload(initialValues.files)
  );

  const update = {
    dependencies: changed(values.dependencies, initialValues.dependencies),
    files,
    // Sent with the files whenever they change, because whether an artifact is
    // a chart follows its paths and dropping the chart has to be as sayable as
    // picking a different one.
    //
    // Sent on its own when only the namespace moved: that is the one thing
    // about a release the paths do not decide, and the endpoint takes it
    // without them. The values files are left to the files branch - they are
    // paths too, and this form starts them empty.
    helm: toHelmUpdate(values, initialValues, isReconciled, !!files),
    // Only the reconciliation engine accepts environment groups and individual
    // environments, and the endpoint rejects them for an Edge stack. Sent as
    // one value so changing any of the three replaces the whole targeting.
    targets: isReconciled
      ? changed(
          {
            edgeGroups: values.targets,
            environmentGroups: values.environmentGroups,
            environments: values.environments,
          },
          {
            edgeGroups: initialValues.targets,
            environmentGroups: initialValues.environmentGroups,
            environments: initialValues.environments,
          }
        )
      : changed(
          { edgeGroups: values.targets },
          { edgeGroups: initialValues.targets }
        ),
    // A isReconciled deployment is Kubernetes by definition, and the endpoint
    // reads this field as an Edge stack deployment type.
    deploymentType: isReconciled
      ? undefined
      : changed(values.deploymentType, initialValues.deploymentType),
    config: toConfigUpdate(values.config, initialValues.config, isReconciled),
  };

  if (!hasChange(update)) {
    return undefined;
  }

  return { id: artifact.id, type: artifact.type, ...update };
}

function toConfigUpdate(
  values: ArtifactConfig,
  initialValues: ArtifactConfig,
  isReconciled: boolean
): WorkflowsUpdateArtifactConfigPayload | undefined {
  if (isReconciled) {
    return toReconciledConfigUpdate(values, initialValues);
  }

  const update = {
    prePullImage: changed(values.prePullImage, initialValues.prePullImage),
    retryPeriod: changed(toRetryPeriod(values), toRetryPeriod(initialValues)),
    envVars: changed(
      toEnvVarsPayload(values.envVars),
      toEnvVarsPayload(initialValues.envVars)
    ),
    registries: changed(
      toRegistries(values.registryId),
      toRegistries(initialValues.registryId)
    ),
    parallelConfig: changed(
      toParallelConfig(values.parallelConfig),
      toParallelConfig(initialValues.parallelConfig)
    ),
    kubernetesPrune: changed(
      values.kubernetesPrune,
      initialValues.kubernetesPrune
    ),
    kubernetesForceApply: changed(
      values.kubernetesForceApply,
      initialValues.kubernetesForceApply
    ),
    alwaysCloneGitRepoForRelativePath: changed(
      values.relativePath.AlwaysCloneGitRepoForRelativePath,
      initialValues.relativePath.AlwaysCloneGitRepoForRelativePath
    ),
  };

  return hasChange(update) ? update : undefined;
}

/**
 * A isReconciled deployment shares only the Kubernetes apply options with the
 * other engine; everything else on the config belongs to an Edge stack and the
 * endpoint has nowhere to put it.
 */
function toReconciledConfigUpdate(
  values: ArtifactConfig,
  initialValues: ArtifactConfig
): WorkflowsUpdateArtifactConfigPayload | undefined {
  const update = {
    useManifestNamespaces: changed(
      values.useManifestNamespaces,
      initialValues.useManifestNamespaces
    ),
    driftMode: changed(values.driftMode, initialValues.driftMode),
    kubernetesPrune: changed(
      values.kubernetesPrune,
      initialValues.kubernetesPrune
    ),
    kubernetesForceApply: changed(
      values.kubernetesForceApply,
      initialValues.kubernetesForceApply
    ),
  };

  return hasChange(update) ? update : undefined;
}

/**
 * The endpoint derives "retry enabled" from the period alone, so a disabled
 * retry has to be sent as zero rather than omitted.
 */
function toRetryPeriod(config: ArtifactConfig): number {
  return config.retryDeploy ? config.retryPeriod : 0;
}

/** An empty list clears the artifact's registries. */
function toRegistries(registryId: number | undefined): Array<number> {
  return registryId ? [registryId] : [];
}

/** A zero batch count switches the artifact back to all-at-once. */
function toParallelConfig(
  config: StaggerConfig
): WorkflowsCreatePayloadArtifactParallelConfig {
  return config.StaggerOption === StaggerOption.Parallel
    ? toParallelDeploymentConfig(config)
    : { batchCount: 0 };
}

/**
 * Reports whether an update asks for nothing, so that saving an unchanged form
 * does not spend a request.
 *
 * Every field is read rather than the few that were interesting when this was
 * written. Naming them meant a field added later was absent from the check, so
 * a change to it alone read as no change at all - which is how editing only the
 * reconcile interval came to report that there was nothing to save while
 * quietly discarding it.
 */
export function isEmptyUpdate(payload: WorkflowsUpdatePayload): boolean {
  return Object.values(payload).every((value) => value === undefined);
}

/**
 * The chart an update declares, or nothing when it says nothing about one.
 *
 * Values files only travel with the files, because they are file paths and the
 * edit form cannot restore them: the artifact carries them in its own file
 * list rather than beside the chart, so a namespace-only edit that sent them
 * would send none and erase the release's overrides.
 */
function toHelmUpdate(
  values: Artifact,
  initialValues: Artifact,
  isReconciled: boolean,
  filesChanged: boolean
): WorkflowsUpdateArtifactPayload['helm'] {
  if (!isReconciled || !values.helm.enabled) {
    return undefined;
  }

  if (filesChanged) {
    return {
      chartPath: values.files[0]?.path,
      valuesFiles: values.helm.valuesFiles,
      namespace: values.helm.namespace || undefined,
    };
  }

  // Only the namespace can have moved without the paths, and saying nothing
  // keeps an unrelated edit from touching the release at all.
  if (values.helm.namespace === initialValues.helm.namespace) {
    return undefined;
  }

  return {
    chartPath: values.files[0]?.path,
    namespace: values.helm.namespace || undefined,
  };
}

function changed<T>(value: T, initialValue: T): T | undefined {
  return isEqual(value, initialValue) ? undefined : value;
}

function hasChange(update: object): boolean {
  return Object.values(update).some((value) => value !== undefined);
}

function isArtifactUpdate(
  update: WorkflowsUpdateArtifactPayload | undefined
): update is WorkflowsUpdateArtifactPayload {
  return !!update;
}
